import { useEffect, useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
}

const shareTargets = [
  { key: "weibo", label: "微博", icon: "ri-weibo-line" },
  { key: "wechat", label: "微信", icon: "ri-wechat-line" },
  { key: "qq", label: "QQ空间", icon: "ri-qq-line" },
  { key: "x", label: "X", icon: "ri-twitter-x-line" },
];

export default function ShareModal({ open, onClose, title, url }: ShareModalProps) {
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!open) return;
    setFeedback("");
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const handleShare = (label: string) => {
    setFeedback(`已模拟分享到${label}，前端演示，未真实发布`);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setFeedback("链接已复制到剪贴板");
    } catch {
      setFeedback("当前浏览器不支持自动复制，请手动复制地址栏链接");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-foreground-950/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="分享爆料"
        className="relative w-full sm:max-w-md bg-background-50 rounded-t-lg sm:rounded-lg border border-background-200 p-5 sm:p-6 animate-fade-up"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-black text-foreground-950">分享这条爆料</h2>
            <p className="mt-1 text-sm text-foreground-500 line-clamp-2">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享框"
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-4 gap-2">
          {shareTargets.map((target) => (
            <button
              key={target.key}
              type="button"
              onClick={() => handleShare(target.label)}
              className="flex flex-col items-center gap-2 py-3 rounded-md border border-background-200 bg-background-100 hover:border-primary-300 hover:bg-background-50 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className={`${target.icon} text-lg`} aria-hidden="true" />
              </span>
              <span className="text-xs text-foreground-700">{target.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            readOnly
            value={url}
            aria-label="爆料链接"
            className="flex-1 min-w-0 h-10 px-3 rounded-md border border-background-200 bg-background-100 text-xs text-foreground-600 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 h-10 px-4 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            复制链接
          </button>
        </div>

        <p className="mt-3 min-h-[18px] text-xs text-accent-700">{feedback}</p>
        <p className="mt-1 text-[11px] text-foreground-400">
          分享功能为前端演示，仅展示交互流程，未接入任何社交平台接口。
        </p>
      </div>
    </div>
  );
}